import { useFormatters } from '@/hooks/useFormatters';
import TimeElapsed from '@components/TimeElapsed';
import { MovementDataRenderer } from '@features/gateCommon';
import { formatPosition } from '@features/gateCommon/utils/formatPosition';
import { Trans } from '@lingui/macro';
import { Skeleton } from '@mui/material';
import { FC } from 'react';

interface Props {
  movementId: number | null;
  attribute:
  | 'startPosition'
  | 'requestedPosition'
  | 'requestedAt'
  | 'requestedBy'
  | 'startTimestamp'
  | 'operationCode'
  | 'id';
}

export const MovementSummaryAttribute: FC<Props> = ({ movementId, attribute }) => {
  const { formatMetricValue, formatDateTime } = useFormatters();

  return movementId ? (
    <MovementDataRenderer
      movementId={movementId}
      render={({ movement }) => {
        if (attribute === 'startPosition' || attribute === 'requestedPosition') {
          const rawPosition = movement[attribute];
          // Un destino ausente no es «no llegó todavía»: la operación manual nunca lo tuvo.
          if (rawPosition == null) {
            return attribute === 'requestedPosition' ? (
              <Trans>Sin destino</Trans>
            ) : (
              <Trans>S/D</Trans>
            );
          }
          return formatPosition(rawPosition[1], formatMetricValue);
        }

        if (attribute === 'requestedBy') {
          if (movement.requestedBy.name) return movement.requestedBy.name;
          // Sin `id` no hubo usuario de Amaru: la abrió el operario en terreno.
          return movement.requestedBy.id == null ? (
            <Trans>Operación manual</Trans>
          ) : (
            <Trans>Sin datos</Trans>
          );
        }

        if (attribute === 'requestedAt') {
          // La fecha de solicitud sale de su propio campo: el `Point` solicitado puede no existir.
          return formatDateTime(movement.requestedAt);
        }

        if (attribute === 'id') {
          return movement.id;
        }

        if (attribute === 'operationCode') {
          // El `mid` del equipo es lo que permite cruzar esta operación con la base local del
          // automatizador en una revisión en terreno. Se agrega al código de Amaru, no lo
          // reemplaza: son dos identificadores distintos de la misma operación.
          return movement.firmwareMovementId != null ? (
            <Trans>
              {movement.id} · equipo {movement.firmwareMovementId}
            </Trans>
          ) : (
            movement.id
          );
        }

        if (attribute === 'startTimestamp') {
          // `startMissing` marca la operación que nació en el cierre: la hora que guarda la fila es
          // la del propio cierre, así que mostrarla como inicio sería inventar un arranque que
          // nadie observó.
          if (movement.startMissing) return <Trans>Inicio no registrado</Trans>;
          return movement.startPosition ? (
            <TimeElapsed timestamp={movement.startPosition[0]} />
          ) : (
            <Trans>Esperando inicio movimiento</Trans>
          );
        }

        return '';
      }}
      /**
       * `width` explícito y **sin texto adentro**.
       *
       * `<Skeleton>Estado dummy</Skeleton>` deja el texto **visible debajo de la barra**: MUI oculta
       * los hijos de un `Skeleton` con `& > *`, que matchea elementos y no un nodo de texto pelado.
       * Se leía «Estado dummy» en cada campo del resumen mientras cargaba.
       *
       * El ancho reserva el del valor típico, así que la fila no se mueve al llegar el dato.
       */
      fallback={() => <Skeleton variant="text" width={110} />}
    />
  ) : (
    <Trans>Iniciando comunicación</Trans>
  );
};
